import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Zap,
  Coins,
  Layers,
  RefreshCw,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { stats, DashboardStats, UsageDataPoint, ModelDistribution } from '../api/client';
import { formatNumber, formatCurrency, cn } from '../lib/utils';

const CHART_COLORS = ['#22d3ee', '#f59e0b', '#8b5cf6', '#10b981', '#f43f5e', '#ec4899'];

export default function Dashboard() {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [usageData, setUsageData] = useState<UsageDataPoint[]>([]);
  const [modelData, setModelData] = useState<ModelDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [statsRes, usageRes, modelsRes] = await Promise.all([
        stats.dashboard(),
        stats.usage(7),
        stats.models(7),
      ]);

      setDashboardStats(statsRes.stats);
      setUsageData(usageRes.data);
      setModelData(modelsRes.distribution);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Real-time AI proxy analytics</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Requests (24h)"
          value={formatNumber(dashboardStats?.totalRequests24h || 0)}
          trend={dashboardStats?.requestsTrend || 0}
          icon={Activity}
          colorClass="stat-card-cyan"
          iconColorClass="text-cyan-400"
          delay={100}
        />
        <StatsCard
          title="Tokens (24h)"
          value={formatNumber(dashboardStats?.totalTokens24h || 0)}
          trend={dashboardStats?.tokensTrend || 0}
          icon={Zap}
          colorClass="stat-card-amber"
          iconColorClass="text-amber-400"
          delay={200}
        />
        <StatsCard
          title="Cost (24h)"
          value={formatCurrency(dashboardStats?.totalCost24h || 0)}
          trend={dashboardStats?.costTrend || 0}
          icon={Coins}
          colorClass="stat-card-emerald"
          iconColorClass="text-emerald-400"
          delay={300}
        />
        <StatsCard
          title="Active Apps"
          value={formatNumber(dashboardStats?.activeApps || 0)}
          icon={Layers}
          colorClass="stat-card-violet"
          iconColorClass="text-violet-400"
          delay={400}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Chart */}
        <div className="lg:col-span-2 card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold">Usage Trend</h2>
              <p className="text-sm text-muted-foreground">Last 7 days</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                <span className="text-muted-foreground">Requests</span>
              </div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageData}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(222 30% 16%)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(val) => val.slice(5)}
                  stroke="hsl(215 20% 45%)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(215 20% 45%)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => formatNumber(val)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(222 47% 10%)',
                    border: '1px solid hsl(222 30% 20%)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px hsl(0 0% 0% / 0.3)',
                  }}
                  labelStyle={{ color: 'hsl(210 40% 96%)' }}
                  itemStyle={{ color: '#22d3ee' }}
                />
                <Area
                  type="monotone"
                  dataKey="requests"
                  stroke="#22d3ee"
                  strokeWidth={2}
                  fill="url(#colorRequests)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Model Distribution */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-600">
          <div className="mb-6">
            <h2 className="text-lg font-semibold">Model Distribution</h2>
            <p className="text-sm text-muted-foreground">Usage breakdown</p>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={modelData}
                  dataKey="count"
                  nameKey="model"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {modelData.map((_, index) => (
                    <Cell
                      key={index}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      stroke="transparent"
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(222 47% 10%)',
                    border: '1px solid hsl(222 30% 20%)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px hsl(0 0% 0% / 0.3)',
                  }}
                  labelStyle={{ color: 'hsl(210 40% 96%)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {modelData.slice(0, 5).map((item, index) => (
              <div key={item.model} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-muted-foreground truncate max-w-[150px]" title={item.model}>
                    {item.model.split('/').pop() || item.model}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {item.percentage.toFixed(0)}%
                  </span>
                  <span className="font-medium">{formatNumber(item.count)}</span>
                </div>
              </div>
            ))}
            {modelData.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No data available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsCard({
  title,
  value,
  trend,
  icon: Icon,
  colorClass,
  iconColorClass,
  delay,
}: {
  title: string;
  value: string;
  trend?: number;
  icon: typeof Activity;
  colorClass: string;
  iconColorClass: string;
  delay: number;
}) {
  return (
    <div
      className={cn(
        'card-glow rounded-2xl p-6 animate-fade-in-up',
        colorClass
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center bg-secondary', iconColorClass)}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg',
              trend > 0
                ? 'bg-emerald-500/15 text-emerald-400'
                : trend < 0
                ? 'bg-rose-500/15 text-rose-400'
                : 'bg-secondary text-muted-foreground'
            )}
          >
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : trend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : (
              <Minus className="w-3 h-3" />
            )}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
        <p className="text-3xl font-bold tracking-tight">{value}</p>
      </div>
    </div>
  );
}
